/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useState, useEffect, useMemo, useRef } from 'react';
import cx from 'classnames';
import { get, isArray, isNil } from 'lodash';

import AssetDropzone from '../../../common/components/AssetDropzone';
import IconButton from '@broadleaf/admin-components/dist/common/components/IconButton';
import useFormatMessage from '@broadleaf/admin-components/dist/common/hooks/useFormatMessage';
import { FieldDecorations } from '@broadleaf/admin-components/dist/form/helpers/FieldDecorations';
import useTranslateMode from '@broadleaf/admin-components/dist/form/hooks/useTranslateMode';
import useAssetDropzoneField from '@broadleaf/admin-components/dist/form/hooks/useAssetDropzoneField';
import {
  getAttribute,
  isFieldDisabled,
  findAction,
  findComponent
} from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import mapper from '@broadleaf/admin-components/dist/common/utils/mapper';
import log from '@broadleaf/admin-components/dist/common/utils/log';
import ItemBrowserProvider from '@broadleaf/admin-components/dist/form/item-browser/providers/ItemBrowserProvider';
import ItemBrowserAssignModal from '@broadleaf/admin-components/dist/form/item-browser/components/ItemBrowserModals/components/ItemBrowserAssignModal';
import useItemBrowser from '@broadleaf/admin-components/dist/form/item-browser/hooks/useItemBrowser';
import { openModal } from '@broadleaf/admin-components/dist/form/item-browser/actions/itemBrowser';
import type { IFormik } from '@broadleaf/admin-components/dist/types/form';
import type {
  IAssetDropzoneMetadata,
  IMetadataFieldComponent
} from '@broadleaf/admin-components/dist/types/metadata';

import messages from './AssetDropzoneField.messages';

const logger = log.getLogger(
  'form.named-components.field-types.AssetDropzoneField'
);

/**
 * The field component that handles uploading an Asset and set its url to the form state.
 * It also allow user to target a specific URL as text.
 *
 * @visibleName ASSET_DROPZONE
 */
export const AssetDropzoneField: React.FC<AssetDropzoneFieldProps> = props => {
  const { formik, metadata, ...otherProps } = props;
  const [showSource, setShowSource] = useState(false);
  const translateMode = useTranslateMode();
  const formatMessage = useFormatMessage();
  const disabled = isFieldDisabled(props, translateMode);

  const error = get(formik.errors, metadata.name);
  const touched = get(formik.touched, metadata.name, false);

  const decorated = props.decorated || metadata.decorated;
  const label = metadata.label;
  const placeholder = metadata.placeholder;
  const displaySource = getAttribute(metadata, 'displaySource', false);
  const maxWidth = getAttribute(metadata, 'maxWidth', 300);
  const maxHeight = getAttribute(metadata, 'maxHeight', 300);
  const ref = useRef<HTMLDivElement>(null);

  const {
    accept,
    asset,
    clearValue,
    handleChange,
    handleSelection,
    handleUpload,
    init,
    onRejectFile,
    srcKey,
    typeKey
  } = useAssetDropzoneField({ formik, metadata });

  useEffect(
    () => {
      init();
    },
    [] // eslint-disable-line
  );

  useEffect(() => {
    if (ref.current) {
      ref.current.style.maxWidth = maxWidth;
    }
  }, [maxWidth]);

  const dropZoneProps = {
    accept,
    disabled,
    emptyMessage: getAttribute(metadata, 'emptyMessage'),
    handleUpload,
    multiple: false,
    onRejectFile,
    srcKey,
    typeKey
  };

  return (
    <div
      ref={ref}
      className={cx('AssetDropzoneField tw-relative', props.className, {
        disabled: !!disabled
      })}
    >
      <div
        className={cx('tw-z-10 tw-flex tw-w-full tw-justify-end tw-px-1', {
          'tw-absolute tw-top-[-0.5rem]': metadata.label
        })}
      >
        <PickExistingAsset
          disabled={disabled}
          onPickExistingAssetSuccess={handleSelection}
          {...props}
        />
        {displaySource && (
          <IconButton
            className="AssetDropzoneField__btn--toggle--source tw-text-gray-600"
            iconClassName="fa-lg"
            disabled={disabled}
            name={showSource ? 'toggle-on' : 'toggle-off'}
            onClick={e => {
              e.preventDefault();
              setShowSource(!showSource);
            }}
            label={formatMessage(messages.toggleSource)}
          />
        )}

        <IconButton
          disabled={!asset || disabled}
          className={
            'AssetDropzoneField__btn--delete hover:tw-no-underline focus:tw-no-underline'
          }
          name="trash"
          onClick={clearValue}
          label={formatMessage(messages.deleteBtnTitle)}
        />
      </div>

      <FieldDecorations
        fullWidth={maxWidth === '100%'}
        formik={formik}
        metadata={metadata}
        {...otherProps}
      >
        {!showSource && (
          <AssetDropzone
            className="AssetDropzoneField__asset--drop--zone tw-mt-2 tw-min-h-[200px] tw-min-w-[200px]"
            {...dropZoneProps}
            style={{ width: maxWidth, height: maxHeight, maxWidth, maxHeight }}
            previewStyle={{
              width: maxWidth,
              height: maxHeight,
              maxWidth,
              maxHeight
            }}
            value={asset}
          />
        )}

        {showSource && (
          <input
            autoComplete="off"
            className={cx(
              'form-control',
              props.className,
              {
                'is-invalid': touched && !!error
              },
              'AssetDropzoneField__asset--source tw-mt-2'
            )}
            type="text"
            disabled={disabled}
            name={metadata.name}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            placeholder={!decorated && !placeholder ? label : placeholder}
            value={get(asset, srcKey, '')}
          />
        )}
      </FieldDecorations>
    </div>
  );
};

export interface AssetDropzoneFieldProps {
  /** Class name for the outer wrapper div. */
  className?: string;
  decorated?: boolean;
  /**  Whether or not this component is currently disabled. */
  disabled?: boolean;
  /** Reference to Formik instance **/
  formik: IFormik;
  /** the metadata for this component */
  metadata: IMetadataFieldComponent &
    IAssetDropzoneMetadata & {
      decorated?: boolean;
    };
}

AssetDropzoneField.defaultProps = {
  disabled: false
};

const PickExistingAsset: React.FC<
  AssetDropzoneFieldProps & { onPickExistingAssetSuccess?: Function }
> = ({ metadata, onPickExistingAssetSuccess, disabled }) => {
  const assignActionDefinition = useMemo(
    () => findAction(metadata, { type: 'ASSIGN' }),
    [metadata]
  );

  const enablePickExistingAsset = !isNil(assignActionDefinition);

  const idKey = getAttribute(metadata, 'idKey', 'id');

  const itemBrowser = useItemBrowser({
    idKey
  });

  const openItemBrowserModal = e => {
    e.preventDefault();
    e.stopPropagation();

    itemBrowser.dispatch(
      openModal('ASSIGN', {
        actionDefinition: assignActionDefinition
      })
    );
  };

  const handlePickExistingAsset = values => {
    return new Promise(resolve => {
      const assetLookupComponent = findComponent(assignActionDefinition, {
        type: 'ASSET_BROWSER_LOOKUP'
      });
      let item = get(values, assetLookupComponent.name);

      const transformSelection = getTransformSelectionMappings(
        assignActionDefinition
      );
      if (isArray(transformSelection)) {
        item = mapper.transform(item, transformSelection);
      }

      onPickExistingAssetSuccess(item);
      return resolve(item);
    });
  };

  const modalState = itemBrowser.state.modals.ASSIGN;

  return (
    <ItemBrowserProvider
      dispatch={itemBrowser.dispatch}
      state={itemBrowser.state}
    >
      {enablePickExistingAsset && (
        <IconButton
          className={'AssetDropzoneField__button--pick--existing--asset'}
          disabled={disabled}
          label={assignActionDefinition.label}
          name="images"
          onClick={openItemBrowserModal}
        />
      )}
      {!isNil(modalState) && (
        <ItemBrowserAssignModal
          handleAssign={handlePickExistingAsset}
          {...modalState.modalProps}
        />
      )}
    </ItemBrowserProvider>
  );
};

/**
 * Helper function used to the the correct mappings for the option selection transformation.
 *
 * @param metadata the metadata
 * @returns {Array}
 */
function getTransformSelectionMappings(metadata) {
  const transformSelection = getAttribute(metadata, 'transformSelection');
  if (isArray(transformSelection)) {
    return transformSelection;
  }

  const deprecatedTransformSelectedValue = getAttribute(
    metadata,
    'transformSelectValue'
  );
  if (isArray(deprecatedTransformSelectedValue)) {
    logger.warn(
      'Found usage of deprecated `transformSelectedValue` within ASSET_DROPZONE field. Please use `transformSelection` instead.'
    );
    return deprecatedTransformSelectedValue;
  }

  return [];
}

export default AssetDropzoneField;
